import { useEffect, useRef, useState } from "react";
import {
  ArrowUp,
  AudioLines,
  Check,
  ChevronDown,
  CircleHelp,
  LoaderCircle,
  Mic,
  Paperclip,
  Settings2,
  Square,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import type { AppState, ProviderStatus, Message } from "../../shared/types";
import type { Action } from "../../shared/actions";
import { useVoice } from "../lib/useVoice";
export function CoachMark({ size = 22 }: { size?: number }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M5 17.5V8.8A3.8 3.8 0 0 1 8.8 5h6.4A3.8 3.8 0 0 1 19 8.8v4.4a3.8 3.8 0 0 1-3.8 3.8H9l-4 3v-2.5Z"
        stroke="currentColor"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="m9 10 1.5-1.5L12 10l1.5-1.5L15 10M9 13h6"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}
interface Props {
  state: AppState;
  provider: ProviderStatus;
  busy: string | null;
  open: boolean;
  setOpen: (v: boolean) => void;
  onSend: (text: string) => Promise<boolean>;
  onAction: (a: unknown[], label?: string) => void;
  onImport: () => void;
  onStop: () => void;
  onError: (text: string) => void;
  localMessages: Message[];
}
export function Composer({
  state,
  provider,
  busy,
  open,
  setOpen,
  onSend,
  onAction,
  onImport,
  onStop,
  onError,
  localMessages,
}: Props) {
  const [draft, setDraft] = useState(
    () => sessionStorage.getItem("cretchess-draft") ?? "",
  );
  const [focused, setFocused] = useState(false);
  const [help, setHelp] = useState(false);
  const [voiceConsent, setVoiceConsent] = useState(false);
  const [hasConsent, setHasConsent] = useState(false);
  const textarea = useRef<HTMLTextAreaElement>(null);
  const scroll = useRef<HTMLDivElement>(null);
  const first = useRef(true);
  const workspace = state.workspace !== null;
  const expanded = !workspace || open;
  const messages = [...state.messages, ...localMessages].sort(
    (a, b) => a.at - b.at,
  );
  const latest = messages.at(-1);
  const sendSequence = useRef(0);
  const send = async (text = draft) => {
    if (!text.trim() || busy) return;
    const sequence = ++sendSequence.current;
    window.speechSynthesis?.cancel();
    voice.stop();
    const saved = draft;
    setDraft("");
    const success = await onSend(text.trim());
    if (!success && sequence === sendSequence.current)
      setDraft((current) => current || saved || text);
  };
  const voice = useVoice(
    (text) => {
      setDraft((current) => (current ? `${current} ${text}` : text));
      textarea.current?.focus();
      if (state.preferences.handsFree) void send(text);
    },
    provider.transcription,
    onError,
  );
  useEffect(() => {
    sessionStorage.setItem("cretchess-draft", draft);
  }, [draft]);
  useEffect(() => {
    if (expanded)
      scroll.current?.scrollTo({
        top: scroll.current.scrollHeight,
        behavior: window.matchMedia("(prefers-reduced-motion: reduce)").matches
          ? "instant"
          : "smooth",
      });
  }, [messages.length, busy, expanded]);
  useEffect(() => {
    if (!workspace && !first.current) {
      const timer = setTimeout(() => textarea.current?.focus(), 500);
      return () => clearTimeout(timer);
    }
    first.current = false;
  }, [workspace]);
  useEffect(() => {
    if (open) {
      const timer = setTimeout(() => textarea.current?.focus(), 300);
      return () => clearTimeout(timer);
    }
  }, [open]);
  useEffect(() => {
    if (
      state.preferences.speech &&
      latest?.role === "assistant" &&
      "speechSynthesis" in window
    ) {
      window.speechSynthesis.cancel();
      const utterance = new SpeechSynthesisUtterance(latest.text);
      utterance.rate = 0.96;
      window.speechSynthesis.speak(utterance);
    }
    return () => window.speechSynthesis?.cancel();
  }, [latest?.id, state.preferences.speech]);
  const shortcuts: { label: string; hint: string; actions: unknown[] }[] = [
    {
      label: "Open a board",
      hint: "Analyze a position",
      actions: [{ type: "workspace.present", workspace: "board" }],
    },
    {
      label: "Play with your coach",
      hint: "White · balanced · no clock",
      actions: [{ type: "game.create", mode: "play", humanColor: "w" }],
    },
    {
      label: "Explore my games",
      hint: "Open a saved or imported game",
      actions: [{ type: "workspace.present", workspace: "library" }],
    },
    {
      label: "Learn something",
      hint: "The art of opposition",
      actions: [{ type: "lesson.create", topic: "opposition" }],
    },
  ];
  return (
    <section
      data-testid="composer-shell"
      className={`composer-shell ${workspace ? "docked" : "centered"} ${workspace && open ? "expanded" : ""} ${voice.listening ? "listening" : ""}`}
      aria-label="Cretchess coach"
    >
      <button
        className="coach-launcher"
        tabIndex={workspace && !open ? 0 : -1}
        aria-hidden={!workspace || open}
        onClick={() => setOpen(true)}
        aria-label="Open coach"
      >
        <CoachMark />
        <span>{busy ? "Thinking" : "Your coach"}</span>
        {busy ? (
          <LoaderCircle size={15} className="spin" />
        ) : messages.length > 0 ? (
          <span className="notification-dot" />
        ) : null}
      </button>
      <div
        className="composer-content"
        inert={!expanded}
        aria-hidden={!expanded}
      >
        {(workspace || messages.length > 0) && (
          <div className="chat-header">
            <div>
              <CoachMark size={20} />
              <span>
                Your coach
                <small>
                  {provider.configured
                    ? "Here to help you think"
                    : "Connect OpenAI to converse"}
                </small>
              </span>
            </div>
            <div className="row">
              <button
                className="icon-button"
                title={
                  state.preferences.speech
                    ? "Mute spoken coaching"
                    : "Enable spoken coaching"
                }
                aria-label={
                  state.preferences.speech
                    ? "Mute spoken coaching"
                    : "Enable spoken coaching"
                }
                onClick={() => {
                  window.speechSynthesis?.cancel();
                  onAction([
                    {
                      type: "coach.configure",
                      speech: !state.preferences.speech,
                    },
                  ]);
                }}
              >
                {state.preferences.speech ? (
                  <Volume2 size={17} />
                ) : (
                  <VolumeX size={17} />
                )}
              </button>
              {workspace && (
                <button
                  className="icon-button"
                  aria-label="Collapse coach"
                  onClick={() => {
                    voice.stop();
                    window.speechSynthesis?.cancel();
                    setOpen(false);
                  }}
                >
                  <ChevronDown size={19} />
                </button>
              )}
            </div>
          </div>
        )}
        {(messages.length > 0 || workspace) && (
          <div
            className="conversation"
            ref={scroll}
            role="log"
            aria-label="Conversation"
            aria-live="polite"
          >
            {!messages.length && (
              <div className="chat-welcome">
                <p>Ask about a move, try a line, or change your game.</p>
              </div>
            )}
            {messages.map((m) => (
              <article className={`message ${m.role}`} key={m.id}>
                <span className="message-author">
                  {m.role === "user"
                    ? "You"
                    : m.role === "system"
                      ? "Cretchess"
                      : "Your coach"}
                </span>
                <p>{m.text}</p>
                {m.reference && (
                  <button
                    className="text-button"
                    onClick={() =>
                      onAction(
                        m.reference!.variationId
                          ? [
                              {
                                type: "variation.load",
                                variationId: m.reference!.variationId,
                              },
                              { type: "game.seek", ply: m.reference!.ply },
                            ]
                          : [
                              {
                                type: "game.load",
                                gameId: m.reference!.gameId,
                                ply: m.reference!.ply,
                              },
                            ],
                      )
                    }
                  >
                    Open this position ↗
                  </button>
                )}
              </article>
            ))}
            {busy && (
              <div className="thinking" role="status">
                <span />
                <span />
                <span />
                {busy}
              </div>
            )}
          </div>
        )}
        <form
          className="composer-form"
          onSubmit={(e) => {
            e.preventDefault();
            void send();
          }}
        >
          <textarea
            ref={textarea}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onFocus={() => setFocused(true)}
            onKeyDown={(e) => {
              if (
                e.key === "Enter" &&
                !e.shiftKey &&
                !e.nativeEvent.isComposing
              ) {
                e.preventDefault();
                void send();
              }
              if (e.key === "Escape" && workspace) setOpen(false);
            }}
            placeholder={
              workspace
                ? "Ask about this position…"
                : "What would you like to do in chess?"
            }
            aria-label="Message your chess coach"
            rows={2}
            maxLength={6000}
          />
          <div className="composer-toolbar">
            <div className="row">
              <button
                className="icon-button"
                type="button"
                aria-label="Import PGN or FEN"
                title="Import a game or position"
                onClick={onImport}
              >
                <Paperclip size={19} />
              </button>
              <button
                className={`icon-button ${help ? "active" : ""}`}
                type="button"
                aria-label="Discover actions"
                title="A few places to start"
                onClick={() => setHelp(!help)}
              >
                <CircleHelp size={18} />
              </button>
              {workspace && (
                <button
                  className="icon-button"
                  type="button"
                  aria-label="Settings"
                  onClick={() =>
                    onAction([
                      { type: "workspace.present", workspace: "settings" },
                    ])
                  }
                >
                  <Settings2 size={17} />
                </button>
              )}
            </div>
            <div className="row">
              {voice.listening && (
                <span className="recording-label">Listening</span>
              )}
              <button
                className={`icon-button mic-button ${voice.listening ? "recording" : ""}`}
                type="button"
                aria-label={
                  voice.listening ? "Stop dictation" : "Start dictation"
                }
                disabled={voice.transcribing}
                onClick={() => {
                  if (hasConsent || voice.listening) void voice.toggle();
                  else setVoiceConsent(true);
                }}
              >
                {voice.transcribing ? (
                  <LoaderCircle size={19} className="spin" />
                ) : voice.listening ? (
                  <AudioLines size={19} />
                ) : (
                  <Mic size={19} />
                )}
              </button>
              {busy ? (
                <button
                  type="button"
                  className="send-button"
                  aria-label="Stop request"
                  onClick={onStop}
                >
                  <Square size={14} fill="currentColor" />
                </button>
              ) : (
                <button
                  type="submit"
                  className="send-button"
                  aria-label="Send message"
                  disabled={!draft.trim()}
                >
                  <ArrowUp size={19} />
                </button>
              )}
            </div>
          </div>
        </form>
        {voiceConsent && (
          <div className="voice-consent">
            <p>
              {window.SpeechRecognition || window.webkitSpeechRecognition
                ? "Your browser’s speech service processes audio and may send it to its provider."
                : "Audio is sent to OpenAI for transcription when configured."}{" "}
              Dictation stays editable
              {state.preferences.handsFree
                ? " (hands-free submission is enabled)"
                : " and is never sent automatically"}
              .
            </p>
            <div className="row">
              <button
                className="small-button primary"
                onClick={() => {
                  setHasConsent(true);
                  setVoiceConsent(false);
                  void voice.toggle();
                }}
              >
                Start dictation
              </button>
              <button
                className="small-button"
                onClick={() => setVoiceConsent(false)}
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
      {!workspace && (
        <div
          className={`canvas-discovery ${focused || help || messages.length ? "visible" : ""}`}
        >
          {(help || focused) && (
            <div className="suggestions">
              {shortcuts.map((item) => (
                <button
                  key={item.label}
                  onClick={() => {
                    setHelp(false);
                    onAction(item.actions);
                  }}
                >
                  <span>{item.label}</span>
                  {help && <small>{item.hint}</small>}
                </button>
              ))}
            </div>
          )}
          {state.activeGameId && (
            <button
              className="restore-link"
              onClick={() => onAction([{ type: "workspace.restore" }])}
            >
              Return to your workspace <span>↗</span>
            </button>
          )}
          <button
            className="quiet-settings"
            aria-label="Open settings"
            onClick={() =>
              onAction([{ type: "workspace.present", workspace: "settings" }])
            }
          >
            Settings & account
          </button>
        </div>
      )}
      {workspace && help && open && (
        <div className="coach-shortcuts">
          <button
            onClick={() =>
              onAction(
                [{ type: "position.analyze", target: "current" }],
                "Analyzing this position",
              )
            }
          >
            Analyze position
          </button>
          <button onClick={() => onAction([{ type: "variation.create" }])}>
            What-If Lab
          </button>
          <button
            onClick={() =>
              onAction(
                [{ type: "practice.create", source: "current" }],
                "Building an exercise",
              )
            }
          >
            Quiz me here
          </button>
        </div>
      )}
    </section>
  );
}
